import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, Stack } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import { ARCHIVAL_STATUS_ARCHIVED } from 'js/constants/pipelineConstants';
import { Checkbox } from 'new/elements/Checkbox/Checkbox';
import { useModals } from 'new/hooks/useModals';
import { ModalMoveToProject } from 'new/elements/ModalMoveToProject';
import { useBulkFlowActions } from './hooks/useBulkFlowActions';
import { BulkActionItem } from './hooks/types';
import { getBulkActionCounts } from './hooks/utils';
import BulkActionPill, { PillActionButton } from './BulkActionPill';
import ModalBulkActionProgress from './ModalBulkActionProgress';

const STATUS_ACTIVE = 'ACTIVE';

const headerSx = {
  px: 0.5,
  py: 0.75,
  minHeight: 36,
  borderBottom: '1px solid',
  borderColor: 'divider',
};
const checkboxSx = { '& .MuiFormControlLabel-label': { fontSize: 12, color: 'text.secondary' } };
const clearLinkSx = { fontSize: 11, fontWeight: 500, lineHeight: 1 };

interface BulkActionsBarProps {
  sources: any[];
  selectedIds: Set<number>;
  isAssigningLoading?: boolean;
  assignContextView?: string;
  onSelectAll: (ids: number[]) => void;
  onDeselectAll: () => void;
  onActionComplete: () => void;
}

const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
  sources,
  selectedIds,
  isAssigningLoading = false,
  assignContextView = 'uncategorized_pipelines',
  onSelectAll,
  onDeselectAll,
  onActionComplete,
}) => {
  const { openModal } = useModals();

  const selectedCount = selectedIds.size;
  const hasFlows = sources.length > 0;
  const someSelected = selectedCount > 0;
  const allSelected = hasFlows && selectedCount === sources.length;

  const handleComplete = useCallback(() => {
    onDeselectAll();
    onActionComplete();
  }, [onDeselectAll, onActionComplete]);

  const { progress, run, cancel, close } = useBulkFlowActions(handleComplete);

  const isRunning = progress.status === 'running' || progress.status === 'cancelling';

  // Center the floating pill on the listing panel (not the viewport) by
  // measuring the header, which spans the panel width and so already sits
  // clear of the side menus.
  const headerRef = useRef<HTMLDivElement>(null);
  const [centerX, setCenterX] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const measure = () => {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0) setCenterX(rect.left + rect.width / 2);
    };
    measure();
    const observer =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : undefined;
    observer?.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [hasFlows, someSelected]);

  const handleSelectAll = useCallback(() => {
    if (allSelected) {
      onDeselectAll();
      return;
    }
    const ids = sources
      .map((source) => source?.origin_node_id)
      .filter((id): id is number => id != null);
    onSelectAll(ids);
  }, [allSelected, sources, onSelectAll, onDeselectAll]);

  const selectedItems = useMemo<BulkActionItem[]>(
    () =>
      sources
        .filter((source) => selectedIds.has(source?.origin_node_id) && source?.flow_node_id != null)
        .map((source) => ({
          flowNodeId: source.flow_node_id,
          name: source.name,
          archived: source.archival_status === ARCHIVAL_STATUS_ARCHIVED,
          active: source.status === STATUS_ACTIVE,
        })),
    [sources, selectedIds],
  );

  const counts = useMemo(() => getBulkActionCounts(selectedItems), [selectedItems]);

  const handleAssign = useCallback(() => {
    openModal(ModalMoveToProject.modalConfig.name, {
      step: 'move',
      contextView: assignContextView,
      onSuccessAssignFlowsToProject: () => {
        onDeselectAll();
        onActionComplete();
      },
      flowIds: Array.from(selectedIds),
    });
  }, [openModal, assignContextView, selectedIds, onDeselectAll, onActionComplete]);

  const pillActions = useMemo<PillActionButton[]>(() => {
    const buttons: PillActionButton[] = [
      {
        id: 'activate',
        label: 'Activate',
        icon: <PlayArrowIcon />,
        count: counts.activate,
        disabled: isRunning || counts.activate === 0,
        disabledTitle: 'No selected flows can be activated',
        onClick: () => run('activate', selectedItems),
      },
      {
        id: 'pause',
        label: 'Pause',
        icon: <PauseIcon />,
        count: counts.pause,
        disabled: isRunning || counts.pause === 0,
        disabledTitle: 'No selected flows can be paused',
        onClick: () => run('pause', selectedItems),
      },
    ];

    if (counts.unarchive > 0) {
      buttons.push({
        id: 'unarchive',
        label: 'Unarchive',
        icon: <UnarchiveOutlinedIcon />,
        count: counts.unarchive,
        disabled: isRunning,
        onClick: () => run('unarchive', selectedItems),
      });
    }

    buttons.push({
      id: 'assign',
      label: 'Assign to Project',
      icon: <DriveFileMoveOutlinedIcon />,
      loading: isAssigningLoading,
      disabled: isRunning,
      onClick: handleAssign,
    });

    return buttons;
  }, [counts, isRunning, isAssigningLoading, selectedItems, run, handleAssign]);

  return (
    <>
      {hasFlows && (
        <Stack ref={headerRef} direction="row" alignItems="center" spacing={1} sx={headerSx}>
          <Checkbox
            subtle
            labelPlacement="end"
            checked={allSelected}
            indeterminate={someSelected && !allSelected}
            onChange={handleSelectAll}
            label={
              someSelected
                ? `${selectedCount} of ${sources.length} selected`
                : `Select all ${sources.length}`
            }
            sx={checkboxSx}
          />
          {someSelected && (
            <Link
              component="button"
              type="button"
              underline="hover"
              onClick={onDeselectAll}
              sx={clearLinkSx}
            >
              Clear
            </Link>
          )}
        </Stack>
      )}

      {someSelected && (
        <BulkActionPill
          selectedCount={selectedCount}
          actions={pillActions}
          onClear={onDeselectAll}
          centerX={centerX}
        />
      )}

      <ModalBulkActionProgress
        open={progress.open}
        action={progress.action}
        total={progress.total}
        applicable={progress.applicable}
        completed={progress.completed}
        succeeded={progress.succeeded}
        failed={progress.failed}
        skipped={progress.skipped}
        status={progress.status}
        log={progress.log}
        currentName={progress.currentName}
        onCancel={cancel}
        onClose={close}
      />
    </>
  );
};

export default BulkActionsBar;
